import Vue from 'vue';
import VueApollo from 'vue-apollo';
import { createApolloClient, restartWebsockets } from 'vue-cli-plugin-apollo/graphql-client';
import { setContext } from 'apollo-link-context';
import { GraphQLDateTime } from 'graphql-iso-date';
import { IntrospectionFragmentMatcher, InMemoryCache } from 'apollo-cache-inmemory';

import getEnvironmentVariable from '@/utils/get-environment-variable';
import { getInstance } from '@/plugins/auth0/use-auth0';
import introspectionQueryResultData from './fragment-types.json';

// Install the vue plugin
Vue.use(VueApollo);

// Name of the localStorage item
const AUTH_TOKEN = 'accessToken';

// Http endpoint
const httpEndpoint = getEnvironmentVariable('VUE_APP_GRAPHQL_HTTP');
if (!httpEndpoint) throw new Error('HTTP API endpoint not defined');
const wsEndpoint = getEnvironmentVariable('VUE_APP_GRAPHQL_WS');
if (!wsEndpoint) throw new Error('WS API endpoint not defined');

const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData,
});
const cache = new InMemoryCache({ fragmentMatcher });

const authLink = setContext(async (request, { headers }) => {
  const token = await getInstance().getTokenSilently();
  console.log(token);
  const authorizationHeader = token ? { authorization: `Bearer ${token}` } : {};
  return {
    headers: {
      ...headers,
      ...authorizationHeader,
    },
  };
});

// Config
const defaultOptions = {
  // You can use `https` for secure connection (recommended in production)
  httpEndpoint,
  // You can use `wss` for secure connection (recommended in production)
  // Use `null` to disable subscriptions
  wsEndpoint,
  // LocalStorage token
  tokenName: AUTH_TOKEN,
  // Enable Automatic Query persisting with Apollo Engine
  persisting: false,
  // Use websockets for everything (no HTTP)
  // You need to pass a `wsEndpoint` for this to work
  websocketsOnly: false,
  // Is being rendered on the server?
  ssr: false,

  // Override default apollo link
  // note: don't override httpLink here, specify httpLink options in the
  // httpLinkOptions property of defaultOptions.
  link: authLink,

  // Override default cache
  cache,

  // Override the way the Authorization header is set
  // getAuth: (tokenName) => ...

  // Additional ApolloClient options
  // apollo: { ... }

  // Client local data (see apollo-link-state)
  // clientState: { resolvers: { ... }, defaults: { ... } }
  typeDefs: 'scalar DateTime',
  resolvers: {
    DateTime: GraphQLDateTime,
  },

  // Error rewrite for re authentication
  apollo: {
    onError(a) {
      console.log(a);
      // if (graphQLErrors) {
      //   for (let err of graphQLErrors) {
      //     // handle errors differently based on its error code
      //     switch (err.extensions.code) {
      //       case 'UNAUTHENTICATED':
      //         // old token has expired throwing AuthenticationError,
      //         // one way to handle is to obtain a new token and
      //         // add it to the operation context
      //         const headers = operation.getContext().headers;
      //         operation.setContext({
      //           headers: {
      //             ...headers,
      //             authorization: getNewToken(),
      //           },
      //         });
      //         // Now, pass the modified operation to the next link
      //         // in the chain. This effectively intercepts the old
      //         // failed request, and retries it with a new token
      //         return forward(operation);

      //       // handle other errors
      //       case 'ANOTHER_ERROR_CODE':
      //       // ...
      //     }
      //   }
      // }
    },
  },
};

// Call this in the Vue app file
export function createProvider(options = {}) {
  // Create apollo client
  const { apolloClient, wsClient } = createApolloClient({
    ...defaultOptions,
    ...options,
  });
  apolloClient.wsClient = wsClient;

  // Create vue apollo provider
  const apolloProvider = new VueApollo({
    defaultClient: apolloClient,
    defaultOptions: {
      $query: {
        // fetchPolicy: 'cache-and-network',
      },
    },
    errorHandler(error) {
      // eslint-disable-next-line no-console
      console.log('%cError', 'background: red; color: white; padding: 2px 4px; border-radius: 3px; font-weight: bold;', error.message);
    },
  });

  return apolloProvider;
}

// Manually call this when user log in
export async function onLogin(apolloClient, token) {
  if (typeof localStorage !== 'undefined' && token) {
    localStorage.setItem(AUTH_TOKEN, token);
  }
  if (apolloClient.wsClient) restartWebsockets(apolloClient.wsClient);
  try {
    await apolloClient.resetStore();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('%cError on cache reset (login)', 'color: orange;', e.message);
  }
}

// Manually call this when user log out
export async function onLogout(apolloClient) {
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem(AUTH_TOKEN);
  }
  if (apolloClient.wsClient) restartWebsockets(apolloClient.wsClient);
  try {
    await apolloClient.resetStore();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('%cError on cache reset (logout)', 'color: orange;', e.message);
  }
}
